import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  CHAT_FILTERS_SLUGS,
  FORECASTING_FILTERS_EVENTS_SLUGS,
  GENERAL_FILTERS_SLUGS,
  HIRING_FILTERS_SLUGS,
  PROJECT_POST_FILTERS_ENUM,
  LIVE_VIEW_FILTERS_SLUGS,
  POSITIONS_SETTINGS_FILTERS_SLUGS,
  PAY_RATES_FILTERS_EVENTS_SLUGS,
  PAYROLL_FILTERS_EVENTS_SLUGS,
  PROJECT_POST_FILTERS_SLUGS,
  PROJECT_SCHEDULING_FILTERS_SLUGS,
  SCHEDULING_BOARD_FILTERS_KEY,
  TIMESHEET_FILTERS_SLUGS,
  TIMESHEET_TAB_FILTERS_SLUGS,
  HELP_DESK_FILTERS_SLUGS
} from '@ubeya/shared-web/constants';
import { FILTERS_IDS } from '../../constants';
import * as api from '../../services/api';
import { uuidv4 } from '../../utils/uuid';
import useAccount from './useAccount';
import useFields, { FieldTypes } from './useFields';
import useContractAccess from './useContractAccess';

const GENDER_FIELD_SLUG = 'gender';

const branchesFilterId = uuidv4();
const approvedFilterId = uuidv4();
const clientApprovalFilterId = uuidv4();
const costCenterFilterId = uuidv4();
const agenciesFilterId = uuidv4();
const clockSourceFilterId = uuidv4();
const payrollProcessId = uuidv4();
const helpDeskBranchesFilterId = uuidv4();
const helpDeskCategoriesFilterId = uuidv4();
const helpDeskAssigneesFilterId = uuidv4();
const helpDeskPriorityFilterId = uuidv4();

const TIMESHEET_TIME_TYPES_FILTERS = [
  GENERAL_FILTERS_SLUGS.WITH_CLOCK_IN,
  GENERAL_FILTERS_SLUGS.WITH_CLOCK_OUT,
  GENERAL_FILTERS_SLUGS.WITH_CHECK_IN,
  GENERAL_FILTERS_SLUGS.WITH_CHECK_OUT,
  GENERAL_FILTERS_SLUGS.WITH_BREAKS
].map((slug) => ({ id: uuidv4(), slug }));

const PROJECT_SCHEDULING_TYPES_FILTERS = [
  PROJECT_SCHEDULING_FILTERS_SLUGS.positions,
  PROJECT_SCHEDULING_FILTERS_SLUGS.locations,
  PROJECT_SCHEDULING_FILTERS_SLUGS.startTime,
  PROJECT_SCHEDULING_FILTERS_SLUGS.endTime,
  PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers,
  PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters,
  GENERAL_FILTERS_SLUGS.SHIFT_TYPES
].map((slug) => ({ id: uuidv4(), slug }));

const SCHEDULING_BOARD_TYPES_FILTERS = [
  PROJECT_SCHEDULING_FILTERS_SLUGS.positions,
  PROJECT_SCHEDULING_FILTERS_SLUGS.mainLocations,
  PROJECT_SCHEDULING_FILTERS_SLUGS.areas,
  PROJECT_SCHEDULING_FILTERS_SLUGS.startTime,
  PROJECT_SCHEDULING_FILTERS_SLUGS.endTime,
  PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers,
  PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters,
  GENERAL_FILTERS_SLUGS.SHIFT_TYPES
].map((slug) => ({ id: uuidv4(), slug, kind: SCHEDULING_BOARD_FILTERS_KEY }));

const useFilters = ({ enabled = true } = {}) => {
  const { accountId } = useAccount({ enabled });
  const { mappedFields } = useFields({ enabled });
  const { enablePayrollProcessFeature, isBlockDemographicFiltersInSchedulingEnabled } = useContext(ConfigContext);
  const { enableContractFeature, enableContractLeaveManagementFeature } = useContractAccess();

  const storeKey = ['filters', accountId];
  const { isLoading, data } = useQuery(storeKey, () => api.getFilters({ accountId }), { enabled });

  const allFiltersDataFull = useMemo(() => data?.data || {}, [data?.data]);

  const allFiltersData = useMemo(() => {
    if (!allFiltersDataFull.employees?.basic) {
      return allFiltersDataFull;
    }

    return {
      ...allFiltersDataFull,
      employees: {
        ...allFiltersDataFull.employees,
        basic: allFiltersDataFull.employees.basic.filter((item) => {
          if (item.slug === GENERAL_FILTERS_SLUGS.ACCOUNT_ROLE) return false;
          if (
            (!enableContractFeature || enableContractLeaveManagementFeature) &&
            item.slug === GENERAL_FILTERS_SLUGS.CONTRACT
          )
            return false;
          if (!enableContractLeaveManagementFeature && item.slug === GENERAL_FILTERS_SLUGS.LEAVE_CONTRACT) return false;
          return true;
        })
      }
    };
  }, [allFiltersDataFull, enableContractFeature, enableContractLeaveManagementFeature]);

  const filters = useMemo(() => {
    const forecastingReportBasic = [
      ...(allFiltersData?.employees?.basic || []),
      ...(allFiltersData?.events?.basic || [])
    ].filter(({ slug }) => FORECASTING_FILTERS_EVENTS_SLUGS.includes(slug));
    forecastingReportBasic?.push({ id: branchesFilterId, slug: GENERAL_FILTERS_SLUGS.BRANCHES });

    const timesheetBasic = [
      ...(allFiltersData?.employees?.basic || []).filter(({ slug }) => TIMESHEET_FILTERS_SLUGS.includes(slug)),
      ...(allFiltersData?.events?.basic || []).filter(({ slug }) => TIMESHEET_FILTERS_SLUGS.includes(slug)),
      { id: agenciesFilterId, slug: GENERAL_FILTERS_SLUGS.AGENCIES },
      { id: approvedFilterId, slug: GENERAL_FILTERS_SLUGS.APPROVED },
      { id: clientApprovalFilterId, slug: GENERAL_FILTERS_SLUGS.CLIENT_APPROVAL },
      { id: costCenterFilterId, slug: GENERAL_FILTERS_SLUGS.COST_CENTER },
      { id: clockSourceFilterId, slug: GENERAL_FILTERS_SLUGS.CLOCK_SOURCE },
      ...TIMESHEET_TIME_TYPES_FILTERS
    ];

    const timesheetTabBasic = [
      ...(allFiltersData?.employees?.basic || []).filter(({ slug }) => TIMESHEET_TAB_FILTERS_SLUGS.includes(slug)),
      ...(allFiltersData?.events?.basic || []).filter(({ slug }) => TIMESHEET_TAB_FILTERS_SLUGS.includes(slug)),
      { id: agenciesFilterId, slug: GENERAL_FILTERS_SLUGS.AGENCIES },
      { id: approvedFilterId, slug: GENERAL_FILTERS_SLUGS.APPROVED },
      { id: clientApprovalFilterId, slug: GENERAL_FILTERS_SLUGS.CLIENT_APPROVAL },
      { id: costCenterFilterId, slug: GENERAL_FILTERS_SLUGS.COST_CENTER },
      { id: clockSourceFilterId, slug: GENERAL_FILTERS_SLUGS.CLOCK_SOURCE },
      ...TIMESHEET_TIME_TYPES_FILTERS
    ];

    const payrollBasic = [
      ...(allFiltersData?.employees?.basic || []).filter(({ id }) =>
        [FILTERS_IDS.POSITIONS_FILTER_ID, FILTERS_IDS.START_EMPLOYMENT_DATE, FILTERS_IDS.DEPARTMENT].includes(id)
      ),
      ...(allFiltersData?.events?.basic || []).filter(({ slug }) => PAYROLL_FILTERS_EVENTS_SLUGS.includes(slug)),
      { id: approvedFilterId, slug: GENERAL_FILTERS_SLUGS.APPROVED },
      { id: clientApprovalFilterId, slug: GENERAL_FILTERS_SLUGS.CLIENT_APPROVAL }
    ];

    if (payrollBasic) {
      payrollBasic.push({ id: costCenterFilterId, slug: GENERAL_FILTERS_SLUGS.COST_CENTER });

      if (enablePayrollProcessFeature) {
        payrollBasic.push({ id: payrollProcessId, slug: 'payrollProcessStatus' });
      }
    }

    const projectSchedulingBasic = PROJECT_SCHEDULING_TYPES_FILTERS.map((currFilter) => {
      return {
        ...currFilter,
        kind: 'project-scheduling'
      };
    });

    const projectSchedulingManageBasic = [
      ...projectSchedulingBasic,
      {
        id: 1,
        slug: PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues
      },
      {
        id: 2,
        slug: PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus
      },
      {
        id: 3,
        slug: PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning
      },
      {
        id: 4,
        slug: PROJECT_SCHEDULING_FILTERS_SLUGS.liveViewStatus
      }
    ]
      .filter(
        ({ slug }) =>
          !isBlockDemographicFiltersInSchedulingEnabled || slug !== PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning
      )
      .map((currFilter) => {
        return {
          ...currFilter,
          kind: 'project-scheduling-manage'
        };
      });

    const bulkEditShiftsHoursBasic = [
      { id: 1, kind: 'bulk-edit-shifts-hours', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.positions },
      { id: 2, kind: 'bulk-edit-shifts-hours', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.locations },
      { id: 3, kind: 'bulk-edit-shifts-hours', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.startTime },
      { id: 4, kind: 'bulk-edit-shifts-hours', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.endTime }
    ];

    const projectLiveViewBasic = [
      { id: 1, kind: 'project-live-view', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.positions },
      { id: 2, kind: 'project-live-view', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.locations },
      { id: 3, kind: 'project-live-view', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.startTime },
      { id: 4, kind: 'project-live-view', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters }
    ];

    const swapShiftBasic = [
      { id: 1, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.positions },
      { id: 2, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.locations },
      { id: 3, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.startTime },
      { id: 4, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.endTime },
      { id: 5, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters },
      { id: 6, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues },
      { id: 7, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus },
      { id: 8, kind: 'swap-shift', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning }
    ].filter(
      ({ slug }) =>
        !isBlockDemographicFiltersInSchedulingEnabled || slug !== PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning
    );

    const projectPostBasic = [
      { id: PROJECT_POST_FILTERS_ENUM.dates, kind: 'project-post', slug: PROJECT_POST_FILTERS_SLUGS.dates },
      { id: PROJECT_POST_FILTERS_ENUM.locations, kind: 'project-post', slug: PROJECT_POST_FILTERS_SLUGS.locations },
      { id: PROJECT_POST_FILTERS_ENUM.positions, kind: 'project-post', slug: PROJECT_POST_FILTERS_SLUGS.positions },
      { id: PROJECT_POST_FILTERS_ENUM.audience, kind: 'project-post', slug: PROJECT_POST_FILTERS_SLUGS.audience }
    ];

    const projectOrderStaffBasic = [
      { id: 1, kind: 'project-order-staff', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.positions },
      { id: 2, kind: 'project-order-staff', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.locations },
      { id: 3, kind: 'project-order-staff', slug: PROJECT_SCHEDULING_FILTERS_SLUGS.startTime }
    ];

    const chatBasic = [
      { id: 1, kind: 'chat', slug: CHAT_FILTERS_SLUGS.tags },
      { id: 2, kind: 'chat', slug: CHAT_FILTERS_SLUGS.branches },
      { id: 3, kind: 'chat', slug: CHAT_FILTERS_SLUGS.statuses },
      { id: 4, kind: 'chat', slug: CHAT_FILTERS_SLUGS.archived }
    ];

    const hiringBasic = [
      { id: 1, kind: 'hiring', slug: HIRING_FILTERS_SLUGS.BRANCHES },
      { id: 2, kind: 'hiring', slug: HIRING_FILTERS_SLUGS.POSITIONS },
      { id: 3, kind: 'hiring', slug: HIRING_FILTERS_SLUGS.STATUS }
    ];

    const liveViewBasic = [
      { id: 1, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.costCenter },
      { id: 2, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.positions },
      { id: 3, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.locations },
      { id: 4, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.areas },
      { id: 5, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.clients },
      { id: 6, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.startTime },
      { id: 7, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.endTime },
      { id: 8, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.eventBooker },
      { id: 9, kind: 'live-scheduling-view', slug: LIVE_VIEW_FILTERS_SLUGS.employeeType }
    ];

    const settingsPositionsBasic = [
      { id: 1, kind: 'settings-positions', slug: POSITIONS_SETTINGS_FILTERS_SLUGS.branches },
      { id: 2, kind: 'settings-positions', slug: POSITIONS_SETTINGS_FILTERS_SLUGS.compliance }
    ];

    const employeesWTDFilteredFields = (allFiltersData?.employees?.fields || [])
      .filter(
        (id) =>
          mappedFields?.[id]?.fieldTypeId === FieldTypes.FIELD_TYPE_OPTIONS ||
          mappedFields?.[id]?.fieldTypeId === FieldTypes.FIELD_TYPE_ADDRESS
      )
      .filter((id) => !mappedFields?.[id]?.isSensitive);
    const employeesWTDBasicFields = (allFiltersData?.employees?.basic || []).filter(({ id }) => FILTERS_IDS.AGE === id);

    const contractLeavesBasicFilterIds = [FILTERS_IDS.AGE];
    const contractLeavesBasicFields = (allFiltersData?.employees?.basic || []).filter(({ id }) =>
      contractLeavesBasicFilterIds.includes(id)
    );
    const contractLeavesFilteredFields = (allFiltersData?.employees?.fields || [])
      .filter(
        (id) =>
          mappedFields?.[id]?.fieldTypeId === FieldTypes.FIELD_TYPE_OPTIONS ||
          mappedFields?.[id]?.fieldTypeId === FieldTypes.FIELD_TYPE_ADDRESS
      )
      .filter((id) => !mappedFields?.[id]?.isSensitive);

    const payRatesFields = {
      basic: [
        ...[
          { id: -1, kind: 'events', slug: GENERAL_FILTERS_SLUGS.BRANCHES },
          { id: -2, kind: 'events', slug: GENERAL_FILTERS_SLUGS.POSITIONS }
        ],
        ...(allFiltersData?.events?.basic || []).filter(({ slug }) => PAY_RATES_FILTERS_EVENTS_SLUGS.includes(slug))
      ],
      fields: []
    };

    const employeesData = allFiltersData?.employees;
    const sensitiveOmittedEmployeeFields = (employeesData?.fields || []).filter(
      (id) => !mappedFields?.[id]?.isSensitive
    );
    const employeesTableFields = employeesData
      ? {
          ...employeesData,
          fields: sensitiveOmittedEmployeeFields
        }
      : {};

    const helpDeskTicketsBasic = [
      { id: helpDeskBranchesFilterId, slug: HELP_DESK_FILTERS_SLUGS.BRANCHES },
      { id: helpDeskCategoriesFilterId, slug: HELP_DESK_FILTERS_SLUGS.CATEGORIES },
      { id: helpDeskAssigneesFilterId, slug: HELP_DESK_FILTERS_SLUGS.ASSIGNEES },
      { id: helpDeskPriorityFilterId, slug: HELP_DESK_FILTERS_SLUGS.PRIORITY }
    ];
    const employeesFilterGroupFields = employeesData
      ? {
          ...employeesData,
          basic: isBlockDemographicFiltersInSchedulingEnabled
            ? (employeesData.basic || []).filter(({ id }) => id !== FILTERS_IDS.AGE)
            : employeesData.basic,
          fields: isBlockDemographicFiltersInSchedulingEnabled
            ? sensitiveOmittedEmployeeFields.filter((id) => mappedFields?.[id]?.slug !== GENDER_FIELD_SLUG)
            : sensitiveOmittedEmployeeFields
        }
      : allFiltersData?.employees;

    return {
      ...allFiltersData,
      ...(employeesFilterGroupFields ? { employees: employeesFilterGroupFields } : {}),
      'forecasting-report': {
        basic: forecastingReportBasic,
        fields: []
      },
      timesheet: {
        basic: timesheetBasic,
        fields: sensitiveOmittedEmployeeFields
      },
      'timesheet-tab': { basic: timesheetTabBasic, fields: [] },
      payroll: { basic: payrollBasic, fields: [] },
      'project-scheduling': {
        basic: projectSchedulingBasic,
        fields: []
      },
      [SCHEDULING_BOARD_FILTERS_KEY]: {
        basic: SCHEDULING_BOARD_TYPES_FILTERS,
        fields: []
      },
      'project-scheduling-manage': {
        basic: projectSchedulingManageBasic,
        fields: []
      },
      'bulk-edit-shifts-hours': {
        basic: bulkEditShiftsHoursBasic,
        fields: []
      },
      'project-live-view': {
        basic: projectLiveViewBasic,
        fields: []
      },
      'swap-shift': {
        basic: swapShiftBasic,
        fields: []
      },
      'project-post': {
        basic: projectPostBasic,
        fields: []
      },
      'project-order-staff': {
        basic: projectOrderStaffBasic,
        fields: []
      },
      chat: {
        basic: chatBasic,
        fields: []
      },
      'live-scheduling-view': {
        basic: liveViewBasic,
        fields: []
      },
      hiring: {
        basic: hiringBasic,
        fields: []
      },
      'settings-positions': {
        basic: settingsPositionsBasic,
        fields: []
      },
      'employees-wtd': { basic: employeesWTDBasicFields, fields: employeesWTDFilteredFields },
      'contract-leaves': { basic: contractLeavesBasicFields, fields: contractLeavesFilteredFields },
      payrates: payRatesFields,
      'employees-table': employeesTableFields,
      'help-desk-tickets': { basic: helpDeskTicketsBasic, fields: [] }
    };
  }, [allFiltersData, enablePayrollProcessFeature, isBlockDemographicFiltersInSchedulingEnabled, mappedFields]);

  return {
    isLoading,
    filters
  };
};

export default useFilters;
